/** Warehouses, zones and bins. Mirrors `core/api/routes/locations.py`. */
import { z } from 'zod';
import { binTypeSchema, lifecycleStatusSchema, zoneTypeSchema } from './enums.js';
import { decimalIn, decimalOut, readableSchema, uuidSchema } from './primitives.js';
export const warehouseSchema = readableSchema.extend({
    code: z.string(),
    name: z.string(),
    address_line1: z.string().nullable().default(null),
    address_line2: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    state: z.string().nullable().default(null),
    pincode: z.string().nullable().default(null),
    country: z.string(),
    gstin: z.string().nullable().default(null),
    is_default: z.boolean(),
    /**
     * Whether an outbound movement may take this warehouse's balance below zero.
     *
     * Off, an oversell is refused with 409 and nothing is written. It is never clamped —
     * Accounts clamps to zero and returns success, leaving a movement the balance does not
     * reflect. A skin should surface this flag on the warehouse, because it changes what a
     * failed goods issue means.
     */
    allow_negative_stock: z.boolean(),
    status: lifecycleStatusSchema,
});
export const warehouseCreateSchema = z.object({
    code: z.string().min(1).max(20),
    name: z.string().min(1).max(200),
    address_line1: z.string().max(255).nullish(),
    address_line2: z.string().max(255).nullish(),
    city: z.string().max(100).nullish(),
    state: z.string().max(100).nullish(),
    pincode: z.string().max(12).nullish(),
    country: z.string().length(2).default('IN'),
    gstin: z.string().max(15).nullish(),
    is_default: z.boolean().default(false),
    allow_negative_stock: z.boolean().default(false),
});
export const warehousePatchSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    address_line1: z.string().max(255).nullable().optional(),
    address_line2: z.string().max(255).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    pincode: z.string().max(12).nullable().optional(),
    country: z.string().length(2).optional(),
    gstin: z.string().max(15).nullable().optional(),
    is_default: z.boolean().optional(),
    allow_negative_stock: z.boolean().optional(),
    status: lifecycleStatusSchema.optional(),
});
export const zoneSchema = readableSchema.extend({
    warehouse_id: uuidSchema,
    code: z.string(),
    name: z.string(),
    zone_type: zoneTypeSchema,
    is_active: z.boolean(),
});
export const zoneCreateSchema = z.object({
    warehouse_id: uuidSchema,
    code: z.string().min(1).max(20),
    name: z.string().min(1).max(200),
    zone_type: zoneTypeSchema.default('STORAGE'),
    is_active: z.boolean().default(true),
});
export const binSchema = readableSchema.extend({
    zone_id: uuidSchema,
    /** Denormalised from the zone and held honest by a three-column foreign key. A bin whose
     *  warehouse disagrees with its zone cannot be inserted, even in raw SQL. */
    warehouse_id: uuidSchema,
    code: z.string(),
    name: z.string(),
    bin_type: binTypeSchema,
    is_pickable: z.boolean(),
    capacity: z.union([decimalOut, z.null()]).default(null),
    is_active: z.boolean(),
});
export const binCreateSchema = z.object({
    /** `warehouse_id` is derived from the zone by the server, never taken from the caller. */
    zone_id: uuidSchema,
    code: z.string().min(1).max(20),
    name: z.string().min(1).max(200),
    bin_type: binTypeSchema.default('STORAGE'),
    is_pickable: z.boolean().default(true),
    capacity: decimalIn({ gt: '0', label: 'capacity' }).nullish(),
    is_active: z.boolean().default(true),
});
export const warehouseCountSchema = z.object({ count: z.number().int() });
//# sourceMappingURL=locations.js.map